<script lang="ts">
	import type { SbSeoMetadata } from '../types';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { formatUpdatedAt } from '$lib/utils/date';
	import { fade, fly } from 'svelte/transition';
	import SbRichtext from '../components/sbRichtext.svelte';
	import { page } from '$app/state';

	interface Props {
		content: ISbStoryData<{
			seo?: SbSeoMetadata;
			content: ISbRichtext;
			custom_last_updated: string;
		}>['content'];
		last_updated: string;
	}

	let { content, last_updated }: Props = $props();
</script>

<svelte:head>
	<title>{content.seo?.title || 'Mill: never waste food again'}</title>
	<meta name="description" content={content.seo?.description} />
	<meta
		property="og:title"
		content={content.seo?.og_title || content.seo?.title || page.data.og_metadata.title}
	/>
	<meta
		property="og:description"
		content={content.seo?.og_description ||
			content.seo?.description ||
			page.data.og_metadata.description}
	/>
	<meta property="og:type" content="website" />
	<meta property="og:url" content={page.data.og_metadata.url} />
	<meta property="og:image" content={content.seo?.og_image || page.data.og_metadata.image} />
	<meta
		property="og:image:secure_url"
		content={content.seo?.og_image || page.data.og_metadata.image}
	/>
</svelte:head>

<section
	class="text-[16px] leading-[150%]"
	out:fade={{ duration: 300 }}
	in:fly={{ y: 40, opacity: 0, duration: 300, delay: 300 }}
>
	<div class="mb-[1ch] text-purple-2">
		Last Updated:
		{#if content.custom_last_updated}
			{formatUpdatedAt(content.custom_last_updated)}
		{:else}
			{formatUpdatedAt(last_updated)}
		{/if}
	</div>
	<article class="text-brown-1">
		<SbRichtext text={content.content} />
	</article>
</section>

<style>
	article {
		:global(a) {
			text-decoration: underline;
			color: theme('colors.green.2');
			font-weight: theme('fontWeight.bold');
		}

		:global(p:empty) {
			margin-bottom: theme('spacing.8');
		}

		:global(h1, h2, h3, h4, h5, h6) {
			font-weight: theme('fontWeight.semibold');
			margin-bottom: theme('spacing.2');
		}

		:global(h1) {
			@apply text-4xl;
		}

		:global(h2) {
			@apply text-3xl;
		}

		:global(h3) {
			@apply text-2xl;
		}

		:global(h4) {
			@apply text-xl;
		}

		:global(h5) {
			@apply text-lg;
		}

		:global(h6) {
			@apply text-base;
		}

		:global(ol) {
			list-style-type: decimal;
			margin-left: theme('spacing.6');

			& ol {
				list-style-type: lower-alpha;
			}
		}

		:global(ul) {
			margin-left: theme('spacing.6');
		}

		:global(span.img-logo img) {
			margin-bottom: theme('spacing.2');
			max-width: theme('spacing.10');
		}
	}
</style>
